import { lazy } from "react";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import RpaMonitorPage from "@/app/rpa/page";
import TaskListPage from "@/app/task-list/page";
import HistoryPage from "@/app/task-list/participated/page";
import ProcessHistoryPage from "@/app/task-list/participated/[processInstanceId]/page";
import HistoricTaskPage from "@/app/task-list/participated/[processInstanceId]/tasks/[taskId]/page";
import StartProcessPage from "@/app/task-list/start-process/page";
import StartFormPage from "@/app/task-list/start-process/[processDefinitionId]/[formKey]/page";
import TaskPage from "@/app/task-list/tasks/[taskId]/page";
import TasksPage from "@/app/task-list/tasks/page";
import TestFormsPage from "@/app/test/page";
import HubSpotTestPage from "@/app/test/hubspot/page";
import { TaskListTasksShell } from "@/app/task-list/_components/TaskListTasksShell";
import {
  HomeRouteComponent,
  OpenTaskRouteComponent,
  RequireAuth,
  RootRouteComponent,
  RouteFallback,
  TaskAreaError,
  TaskAreaRouteComponent,
} from "@/app/route-components";

const AccountResultsPage = lazy(() => import("@/app/task-list/gestion-cuentas/[processInstanceId]/resultados/page"));

const rootRoute = createRootRoute({
  component: RootRouteComponent,
  notFoundComponent: () => <RouteFallback label="Ruta no encontrada" />,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRouteComponent,
});

const taskAreaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "task-list",
  component: TaskAreaRouteComponent,
  errorComponent: TaskAreaError,
});

const taskAreaIndexRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "/",
  component: TaskListPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "tasks",
  component: () => <TaskListTasksShell><Outlet /></TaskListTasksShell>,
});

const tasksIndexRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: "/",
  component: TasksPage,
});

const taskDetailRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: "$taskId",
  component: () => <TaskPage params={taskDetailRoute.useParams()} />,
});

const startProcessRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "start-process",
  component: Outlet,
});

const startProcessIndexRoute = createRoute({
  getParentRoute: () => startProcessRoute,
  path: "/",
  component: StartProcessPage,
});

const startFormRoute = createRoute({
  getParentRoute: () => startProcessRoute,
  path: "$processDefinitionId/$formKey",
  component: () => <StartFormPage params={startFormRoute.useParams()} />,
});

const participatedRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "participated",
  component: Outlet,
});

const participatedIndexRoute = createRoute({
  getParentRoute: () => participatedRoute,
  path: "/",
  component: HistoryPage,
});

const processHistoryRoute = createRoute({
  getParentRoute: () => participatedRoute,
  path: "$processInstanceId",
  component: () => <ProcessHistoryPage params={processHistoryRoute.useParams()} />,
});

const historicTaskRoute = createRoute({
  getParentRoute: () => participatedRoute,
  path: "$processInstanceId/tasks/$taskId",
  component: () => <HistoricTaskPage params={historicTaskRoute.useParams()} />,
});

const openTaskRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "open",
  component: OpenTaskRouteComponent,
});

const accountResultsRoute = createRoute({
  getParentRoute: () => taskAreaRoute,
  path: "gestion-cuentas/$processInstanceId/resultados",
  component: () => <AccountResultsPage params={accountResultsRoute.useParams()} />,
});

const rpaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "rpa",
  component: () => <RequireAuth><RpaMonitorPage /></RequireAuth>,
});

const testRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "test",
  component: () => <RequireAuth><Outlet /></RequireAuth>,
});

const testIndexRoute = createRoute({
  getParentRoute: () => testRoute,
  path: "/",
  component: TestFormsPage,
});

const testHubSpotRoute = createRoute({
  getParentRoute: () => testRoute,
  path: "hubspot",
  component: HubSpotTestPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  taskAreaRoute.addChildren([
    taskAreaIndexRoute,
    tasksRoute.addChildren([tasksIndexRoute, taskDetailRoute]),
    startProcessRoute.addChildren([startProcessIndexRoute, startFormRoute]),
    participatedRoute.addChildren([
      participatedIndexRoute,
      processHistoryRoute,
      historicTaskRoute,
    ]),
    openTaskRoute,
    accountResultsRoute,
  ]),
  rpaRoute,
  testRoute.addChildren([testIndexRoute, testHubSpotRoute]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
